import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LEGAL_VERSION } from "@/lib/legal";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import {
  CheckCircle,
  Gauge,
  MusicNotes,
  UploadSimple,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function defaultCallDateTime() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const PROCESSING_STEPS = [
  {
    label: "Transcribing audio",
    detail: "Turning the recording into a structured conversation.",
  },
  {
    label: "Dissecting conversation",
    detail: "Revealing themes, signals, coaching and next steps.",
  },
];

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState("");
  const [occurredAt, setOccurredAt] = useState(defaultCallDateTime);
  const [recordingAuthority, setRecordingAuthority] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [progressStep, setProgressStep] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [betaLimits, setBetaLimits] = useState(null);
  const inputRef = useRef();
  const nav = useNavigate();

  const cleanTitle = title.trim();
  const cleanCustomer = customer.trim();
  const quotaExhausted = Boolean(
    betaLimits?.quota_enforced && betaLimits?.uploaded_calls_remaining === 0
  );
  const maxUploadMb = betaLimits?.max_upload_mb || 100;
  const maxAudioMinutes = betaLimits?.max_audio_minutes || 90;
  const canSubmit =
    Boolean(file) &&
    Boolean(cleanTitle) &&
    Boolean(cleanCustomer) &&
    Boolean(occurredAt) &&
    recordingAuthority &&
    !quotaExhausted &&
    !busy;

  useEffect(() => {
    let active = true;

    api
      .get("/calls")
      .then(({ data }) => {
        if (!active || !Array.isArray(data)) return;
        setTitleSuggestions(uniqueValues(data.map((call) => call.title)));
        setCustomerSuggestions(
          uniqueValues(
            data.flatMap((call) => [call.customer_name, call.company]),
          ),
        );
      })
      .catch(() => {
        // Suggestions are an enhancement; uploading must remain available.
      });

    api
      .get("/beta/limits")
      .then(({ data }) => {
        if (active) setBetaLimits(data);
      })
      .catch(() => {
        // The backend remains authoritative if allowance visibility cannot load.
      });

    return () => {
      active = false;
    };
  }, []);

  const onPick = (pickedFile) => {
    if (!pickedFile || busy) return;
    if (quotaExhausted) {
      toast.error("Your private-beta call allowance has been used.");
      return;
    }
    if (pickedFile.size > maxUploadMb * 1024 * 1024) {
      toast.error(`This file exceeds the ${maxUploadMb} MB beta limit.`);
      return;
    }

    const okTypes = [
      "audio/mpeg",
      "audio/mp3",
      "audio/wav",
      "audio/x-wav",
      "audio/aac",
      "audio/mp4",
      "audio/m4a",
      "audio/x-m4a",
    ];

    if (
      !okTypes.includes(pickedFile.type) &&
      !/\.(mp3|wav|aac|m4a)$/i.test(pickedFile.name)
    ) {
      toast.error("Please choose an mp3, wav, aac or m4a file");
      return;
    }

    setFile(pickedFile);
    if (!title.trim()) setTitle(pickedFile.name.replace(/\.[^.]+$/, ""));
  };

  const clearFile = () => {
    if (busy) return;
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const openFilePicker = () => {
    if (quotaExhausted) {
      toast.error("Your private-beta call allowance has been used.");
      return;
    }
    if (!busy) inputRef.current?.click();
  };

  const submit = async (event) => {
    event.preventDefault();
    setValidationAttempted(true);

    if (quotaExhausted) return toast.error("Your private-beta call allowance has been used.");
    if (!file) return toast.error("Choose an audio file first");
    if (!cleanTitle) return toast.error("Add a call title");
    if (!cleanCustomer) return toast.error("Add a customer or company name");
    if (!recordingAuthority) {
      return toast.error("Confirm that you are authorised to process the recording");
    }

    const callDate = new Date(occurredAt);
    if (!occurredAt || Number.isNaN(callDate.getTime())) {
      return toast.error("Choose a valid call date and time");
    }

    setBusy(true);
    setProgressStep(1);
    setProgress("Transcribing audio...");

    const formData = new FormData();
    formData.append("audio", file);
    formData.append("title", cleanTitle);
    formData.append("customer_name", cleanCustomer);
    formData.append("occurred_at", callDate.toISOString());
    formData.append(
      "recording_authority_confirmed",
      String(recordingAuthority),
    );
    formData.append("legal_version", LEGAL_VERSION);

    try {
      const { data } = await api.post("/calls/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 1800000,
      });

      setProgressStep(2);
      setProgress("Dissecting conversation...");

      let analysisCompleted = false;
      try {
        await api.post(`/calls/${data.id}/analyze`);
        analysisCompleted = true;
      } catch (error) {
        // Analysis is non-blocking — the call itself was uploaded successfully.
        console.warn("Auto-analysis failed", error);
      }

      if (analysisCompleted) {
        setProgressStep(3);
        setProgress("Dissection complete");
        await new Promise((resolve) => setTimeout(resolve, 1400));
        toast.success("Call dissected");
      } else {
        toast.warning(
          "Call uploaded. Analysis can be retried from the call page.",
        );
      }

      nav(`/calls/${data.id}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      setProgressStep(0);
      setProgress("");
    }
  };

  return (
    <div className="upload-page min-h-screen">
      <Nav />

      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-14">
        <AppBreadcrumbs items={[{ label: "New Call" }]} />
        <header className="mb-8 max-w-3xl sm:mb-10">
          <div className="brand-eyebrow">Conversation intake</div>
          <h1 className="mt-4 text-4xl font-semibold tracking-[-0.045em] text-white sm:text-5xl">
            Dissect a new call
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
            Add the recording and its context. Callsect will turn the conversation
            into structured findings, coaching and CRM-ready notes.
          </p>
        </header>

        {betaLimits?.quota_enforced ? (
          <BetaAllowancePanel limits={betaLimits} />
        ) : null}

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_19rem]">
          <form
            onSubmit={submit}
            className="upload-form surface-card overflow-visible p-4 sm:p-6 lg:p-7"
          >
            <div
              className={`upload-dropzone ${dragActive ? "is-dragging" : ""} ${
                file ? "has-file" : ""
              } ${busy ? "is-disabled" : ""}`}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                if (!busy) setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!busy) setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setDragActive(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                onPick(event.dataTransfer.files?.[0]);
              }}
              role="button"
              tabIndex={busy ? -1 : 0}
              aria-disabled={busy}
              data-testid="dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept=".mp3,.wav,.aac,.m4a,audio/*"
                className="hidden"
                onChange={(event) => onPick(event.target.files?.[0])}
                disabled={busy}
                data-testid="file-input"
              />

              <div className="upload-dropzone__glow" aria-hidden="true" />

              {file ? (
                <div className="relative z-10 flex w-full items-center gap-4 text-left">
                  <div className="upload-file-mark" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-medium text-white">
                      {file.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      <span className="hidden h-1 w-1 rounded-full bg-slate-700 sm:block" />
                      <span>Ready to dissect</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      clearFile();
                    }}
                    disabled={busy}
                    className="upload-clear-button"
                    aria-label="Remove selected audio file"
                    data-testid="clear-file-btn"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="relative z-10 flex flex-col items-center">
                  <div className="upload-icon-shell" aria-hidden="true">
                    <UploadSimple size={30} weight="light" />
                  </div>
                  <div className="mt-5 text-base font-medium text-white sm:text-lg">
                    Drop audio here or click to browse
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                    mp3 · wav · aac · m4a · max {maxUploadMb}MB
                  </div>
                </div>
              )}
            </div>

            <div className="my-6 segmented-divider" />

            <fieldset disabled={busy} className="space-y-5 disabled:opacity-65">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field
                  label="Call title"
                  value={title}
                  setValue={setTitle}
                  testId="title-input"
                  placeholder="Renewal call - Q3"
                  suggestions={titleSuggestions}
                  required
                  showError={validationAttempted}
                  errorMessage="Call title is required."
                />
                <Field
                  label="Customer"
                  hint="Customer or company name"
                  value={customer}
                  setValue={setCustomer}
                  testId="customer-input"
                  placeholder="John Griffin or Acme Corp"
                  suggestions={customerSuggestions}
                  required
                  showError={validationAttempted}
                  errorMessage="Customer or company name is required."
                />
              </div>

              <div>
                <label className="upload-field-label" htmlFor="occurred-at">
                  Call date and time
                </label>
                <input
                  id="occurred-at"
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                  data-testid="occurred-at-input"
                  className="upload-input mt-2"
                />
                <div className="mt-2 text-xs leading-5 text-slate-600">
                  Used to place this conversation correctly in the relationship
                  timeline.
                </div>
              </div>

              <label className="upload-consent">
                <input
                  type="checkbox"
                  checked={recordingAuthority}
                  onChange={(event) =>
                    setRecordingAuthority(event.target.checked)
                  }
                  className="mt-1 h-4 w-4 shrink-0 accent-violet-500"
                  data-testid="recording-authority-checkbox"
                />
                <span>
                  I confirm that I am authorised to upload and process this recording
                  and have provided any notice or obtained any consent required by
                  applicable law. See the{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className="text-violet-300 transition hover:text-violet-200"
                  >
                    Privacy Notice
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/data-handling"
                    target="_blank"
                    className="text-violet-300 transition hover:text-violet-200"
                  >
                    data-handling summary
                  </Link>
                  .
                </span>
              </label>
            </fieldset>

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="upload-submit-btn"
              className="upload-submit-button mt-6"
            >
              {busy ? progress || "Working..." : "Dissect call"}
            </button>

            {busy && (
              <ProcessingPanel progressStep={progressStep} progress={progress} />
            )}
          </form>

          <aside className="surface-card overflow-hidden lg:sticky lg:top-24">
            <div className="p-5 sm:p-6">
              <div className="mb-5 rounded-xl border border-white/[0.07] bg-white/[0.025] p-4 text-xs leading-5 text-slate-500">
                <div className="flex items-center gap-2 font-semibold uppercase tracking-[0.15em] text-cyan-300">
                  <Gauge size={16} /> Beta limits
                </div>
                <div className="mt-2">Up to {maxAudioMinutes} minutes and {maxUploadMb} MB per audio file.</div>
                <div className="mt-1">Duration is verified before external processing begins.</div>
              </div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-300">
                What happens next
              </div>
              <div className="mt-5 space-y-5">
                <ProcessNote
                  number="01"
                  title="Conversation"
                  copy="The recording is transcribed with speakers and timing preserved."
                />
                <ProcessNote
                  number="02"
                  title="Dissection"
                  copy="Signals, objections, themes and context are separated into clear layers."
                />
                <ProcessNote
                  number="03"
                  title="Action"
                  copy="Findings, coaching and CRM notes are prepared for immediate use."
                />
              </div>
            </div>
            <div className="segmented-divider" />
            <div className="flex items-center gap-3 px-5 py-4 text-xs leading-5 text-slate-500 sm:px-6">
              <MusicNotes size={18} className="shrink-0 text-cyan-400/80" />
              Your original recording and generated outputs remain manageable from the
              call page.
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function BetaAllowancePanel({ limits }) {
  const remaining = limits.uploaded_calls_remaining;
  const used = limits.uploaded_calls_used || 0;
  const total = limits.uploaded_calls_limit;
  const level = limits.uploaded_calls_warning?.level || "normal";
  const exhausted = level === "exhausted" || remaining === 0;
  const near = level === "near" || remaining === 1;

  return (
    <section
      className={`mb-6 flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
        exhausted
          ? "border-rose-400/25 bg-rose-400/[0.06]"
          : near
            ? "border-amber-400/25 bg-amber-400/[0.055]"
            : "border-white/[0.08] bg-white/[0.025]"
      }`}
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        {exhausted || near ? (
          <WarningCircle
            size={22}
            weight="duotone"
            className={exhausted ? "mt-0.5 text-rose-300" : "mt-0.5 text-amber-300"}
          />
        ) : (
          <Gauge size={22} weight="duotone" className="mt-0.5 text-violet-300" />
        )}
        <div>
          <div className="text-sm font-semibold text-white">Private-beta allowance</div>
          <div className="mt-1 text-xs leading-5 text-slate-400">
            {exhausted
              ? "Your call allowance has been used. New uploads are disabled."
              : near
                ? "You have one call remaining. Choose the next upload carefully."
                : `${remaining} calls remaining. Each file can be up to ${limits.max_audio_minutes} minutes and ${limits.max_upload_mb} MB.`}
          </div>
        </div>
      </div>
      <div className="shrink-0 rounded-xl border border-white/[0.08] bg-black/15 px-4 py-2.5 text-right">
        <div className="text-xl font-semibold text-white">{used}/{total}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">calls used</div>
      </div>
    </section>
  );
}


function ProcessingPanel({ progressStep, progress }) {
  const complete = progressStep === 3;

  return (
    <section
      className={`processing-panel mt-5 ${complete ? "is-complete" : ""}`}
      role="status"
      aria-live="polite"
      data-testid={complete ? "dissection-complete" : "dissection-progress"}
    >
      <div className="processing-panel__visual" aria-hidden="true">
        {complete ? (
          <CheckCircle size={44} weight="fill" />
        ) : (
          <div
            className={`dissection-lines ${
              progressStep === 2 ? "is-dissecting" : "is-transcribing"
            }`}
          >
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-white">
              {complete ? "Dissection complete" : progress}
            </div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              {complete
                ? "Opening your call intelligence..."
                : progressStep === 1
                  ? "Keep this page open while the audio is processed."
                  : "The conversation is being separated into actionable layers."}
            </div>
          </div>
          {!complete && (
            <div className="processing-panel__stage">
              Stage {Math.min(progressStep, 2)} of 2
            </div>
          )}
        </div>

        {!complete && (
          <div className="mt-4 space-y-3">
            {PROCESSING_STEPS.map((step, index) => {
              const stepNumber = index + 1;
              const isComplete = progressStep > stepNumber;
              const isActive = progressStep === stepNumber;

              return (
                <div
                  key={step.label}
                  className={`processing-step ${
                    isComplete ? "is-complete" : ""
                  } ${isActive ? "is-active" : ""}`}
                >
                  <div className="processing-step__marker">
                    {isComplete ? "✓" : stepNumber}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{step.label}</div>
                    <div className="mt-0.5 text-xs text-slate-600">
                      {step.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function ProcessNote({ number, title, copy }) {
  return (
    <div className="flex gap-3">
      <div className="process-note__number">{number}</div>
      <div>
        <div className="text-sm font-medium text-slate-100">{title}</div>
        <div className="mt-1 text-xs leading-5 text-slate-500">{copy}</div>
      </div>
    </div>
  );
}

function uniqueValues(values) {
  const unique = new Map();

  values.forEach((value) => {
    const clean = typeof value === "string" ? value.trim() : "";
    if (!clean) return;
    const key = clean.toLocaleLowerCase();
    if (!unique.has(key)) unique.set(key, clean);
  });

  return [...unique.values()].sort((a, b) => a.localeCompare(b));
}

function Field({
  label,
  hint,
  value,
  setValue,
  testId,
  placeholder,
  suggestions = [],
  required = false,
  showError = false,
  errorMessage,
}) {
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const query = value.trim().toLocaleLowerCase();
  const inputId = `${testId}-field`;
  const errorId = `${testId}-error`;
  const hasError = required && !query && (touched || showError);
  const matches =
    query.length >= 2
      ? suggestions
          .filter((suggestion) => {
            const normalized = suggestion.toLocaleLowerCase();
            const beginsWithQuery = normalized
              .split(/\s+/)
              .some((word) => word.startsWith(query));
            return beginsWithQuery && normalized !== query;
          })
          .slice(0, 6)
      : [];

  return (
    <div className="relative">
      <label className="upload-field-label" htmlFor={inputId}>
        {label}
        {required && (
          <>
            <span className="upload-field-required" aria-hidden="true">
              *
            </span>
            <span className="sr-only"> required</span>
          </>
        )}
        {hint && (
          <span className="ml-2 font-normal normal-case tracking-normal text-slate-600">
            ({hint})
          </span>
        )}
      </label>
      <input
        id={inputId}
        data-testid={testId}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setTouched(true);
          window.setTimeout(() => setFocused(false), 120);
        }}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={focused && matches.length > 0}
        aria-required={required}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        required={required}
        className={`upload-input mt-2${hasError ? " is-invalid" : ""}`}
      />
      {hasError && (
        <div id={errorId} className="upload-field-error" role="alert">
          {errorMessage || `${label} is required.`}
        </div>
      )}
      {focused && matches.length > 0 && (
        <div
          role="listbox"
          className="upload-suggestions"
          data-testid={`${testId}-suggestions`}
        >
          {matches.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              role="option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setValue(suggestion);
                setFocused(false);
              }}
              className="upload-suggestion"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
